import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function Economics40() {

   const contentRef = useRef(null);
         const links = {
        prev: "/economics-39", 
        index: "/index-economics",                 
        next: "/economics-41", 
      };
        const titleText ="The Destruction Caused by Inflation";

        const questions = [
  {
    text: "1. What is the fundamental error made by the most naive supporters of inflation?",
    options: [
      { value: "A", label: "A. They believe inflation helps reduce imports" },
      { value: "B", label: "B. They think inflation benefits creditors" },
      {
        value: "C",
        label: "C. They assume increasing the money supply increases real wealth",
      },
      {
        value: "D",
        label: "D. They argue inflation should only be used during war",
      },
    ],
    correct: "C",
  },
  {
    text: "2. What is the belief held by the \"less naive\" inflationists regarding money printing?",
    options: [
      { value: "A", label: "A. It should be avoided completely" },
      {
        value: "B",
        label: "B. It is beneficial for reducing national debt",
      },
      {
        value: "C",
        label:
          "C. It should only be done to fill an alleged “gap” in payments",
      },
      {
        value: "D",
        label: "D. It will solve unemployment immediately",
      },
    ],
    correct: "C",
  },
  {
    text: "3. According to the lesson, what is the eventual effect of printing money on purchasing power?",
    options: [
      {
        value: "A",
        label: "A. It stabilizes prices across all sectors",
      },
      {
        value: "B",
        label: "B. It increases purchasing power equally for everyone",
      },
      {
        value: "C",
        label:
          "C. It lowers the value of money, causing prices to rise",
      },
      {
        value: "D",
        label: "D. It reduces the need for taxation",
      },
    ],
    correct: "C",
  },
  {
    text: "4. Who benefits the most from inflation in its early stages, as per the discussed cycle of spending?",
    options: [
      { value: "A", label: "A. Government creditors" },
      {
        value: "B",
        label: "B. The last group to receive new money",
      },
      { value: "C", label: "C. Exporters only" },
      {
        value: "D",
        label: "D. The first recipients of the newly printed money",
      },
    ],
    correct: "D",
  },
];

   // State to track selected answers for all questions
        const [answers, setAnswers] = useState({});
      
        // Function to update an answer
        const handleAnswerChange = (questionIndex, selectedValue) => {
          setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
        };
      
        // Compute score
        const score = questions.reduce((total, q, index) => {
          return total + (answers[index] === q.correct ? 1 : 0);
        }, 0);
      
  

  return (
    <div className="container">
     <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={40}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 40</b>! Today, we begin exploring a topic that affects
        every one of us, whether we realize it or not: inflation. At its core,
        inflation means that the money in your hand slowly loses value over
        time. The same amount of money buys you less than it did before. For
        example, something that cost $100 last year might cost $105 or $110 this
        year, not because it became better, but because the value of money fell.
        This happens when more money is introduced into the economy without a
        matching increase in goods and services. As a result, prices rise, and
        each unit of currency holds less purchasing power than before. But why
        does this happen? Is it natural, or is it caused by someone? How much
        inflation is "normal"? Can it be controlled? And most importantly, who
        benefits from inflation and who gets hurt by it? In this lesson, we’ll
        break inflation down into simple, relatable concepts using real-world
        examples. By the end of today, you’ll understand what inflation truly
        is, not just as a number on the news, but as a force that shapes
        economies, policies, and your daily life.
        <br />
        <br />
        <b>Money, Wealth and Inflation</b>
        <br />
        <br />
        Throughout the previous lessons, we have examined various economic
        policies, their intentions, and their immediate outcomes. However, we
        have mostly left one crucial factor out of the discussion, which is
        inflation. When we evaluate the effects of economic policies, we often
        do so without factoring in the corrosive and long-term consequences of
        inflation. Yet, inflation is the silent destroyer that undermines
        economic stability and long-term prosperity. It is not simply a rise in
        prices but a deliberate policy of reducing the purchasing power of a
        nation's legal tender. Governments often engage in this act to fulfill
        their fiscal and political agendas, most commonly by printing new money
        or digitally injecting liquidity into the system. The real damage,
        however, occurs over time, gradually hollowing out the productive
        capacity and trust in the economy.
        <br />A major reason for this widespread acceptance of inflationary
        policies is the general confusion between money and wealth. Money is a
        medium of exchange, a tool to measure and facilitate trade, but it is
        not wealth in and of itself. Wealth is represented by the tangible goods
        and services we produce and consume: the food we eat, the clothes we
        wear, the homes we live in, the machines we build, and the knowledge we
        apply. Misidentifying money as wealth has led many down the road of
        supporting inflationary strategies, mistakenly believing that increasing
        the money supply automatically enriches society. However, money is only
        as valuable as what it can buy. If the amount of money doubles while the
        production of goods and services remains the same, then prices will
        inevitably rise, making each unit of money worth less.
        <br />
        The most naive of the inflationists believe that printing money is a
        direct route to prosperity. If people lack money, why not just print
        more and give it to them? By this logic, poverty could be ended
        overnight. But such thinking ignores the real constraint in any economy,
        which is scarcity of resources. Unlike money, which can be created with
        a keystroke, goods and services require labor, raw materials, time, and
        coordination to produce. One cannot double the output of farms,
        factories, and transportation systems merely by printing more currency.
        In fact, injecting more money into the system without a corresponding
        increase in goods will only serve to raise prices and distort markets.
        These naive thinkers confuse nominal increases (more money) with real
        increases (more goods), leading to dangerous policies that fail to
        address the root causes of poverty and stagnation.
        <br />
        <br />
        <b>Layers of Misunderstanding Among Inflationists</b>
        <br />
        <br />
        There exists a slightly less naive group of inflationists who reject the
        idea of printing unlimited money but still believe that the government
        should step in to fill so-called "gaps" in the economy. They talk of
        "deficiencies in demand" or "shortfalls in purchasing power," often
        arguing that if only the government could inject the right amount of
        money at the right time, the economy would achieve perfect equilibrium.
        While more sophisticated than the naive view, this approach still
        suffers from a fatal misunderstanding: the belief that economic systems
        can be finely tuned by central planners using the money supply as their
        instrument. These inflationists often fall into the trap of
        double-counting economic inputs and outputs, confusing accounting
        identities with economic reality.
        <br />
        For example, some of them divide the economy into "A payments" and "A+B
        payments," declaring that production generates income only equal to A,
        while consumption requires income equal to A plus B. Their conclusion is
        that there must always be a deficiency of money, and this deficiency
        must be corrected by government-issued credits or subsidies. Entire
        political movements have arisen based on these erroneous assumptions.
        They print leaflets filled with obscure economic diagrams, and lobby
        governments to "correct" the imbalance by creating new money. What they
        fail to see is that production and consumption are part of a dynamic,
        self-correcting process. When you produce, you earn the right to
        consume. Any persistent gap between supply and demand is usually the
        result of intervention, not the lack of intervention.
        <br />
        Then there are the most sophisticated of inflationists, who not only
        understand that inflation reduces the purchasing power of money but also
        believe that this is a feature, not a bug. They argue that inflation
        helps debtors at the expense of creditors, stimulates exports by making
        goods cheaper in foreign markets, discourages imports, and encourages
        economic activity by penalizing the hoarding of money. These arguments
        have a veneer of intellectual legitimacy, but they crumble under
        scrutiny. Yes, inflation may help debtors repay loans in cheaper money,
        but it punishes savers and fixed-income earners. Yes, it may give
        temporary relief to exporters, but it does so by eroding domestic
        purchasing power and trust in the currency. Ultimately, these policies
        result in a race to the bottom, where every country competes to debase
        its currency faster than the next, leaving the global economy on
        unstable footing.
        <br />
        Moreover, they ignore the unseen consequences of inflation. Any benefit
        to one group inevitably comes at the cost of another. Inflation is not a
        victimless event; it redistributes wealth arbitrarily, rewarding those
        who receive the new money early and punishing those who receive it late
        or not at all. When governments inflate the currency to pay for wars,
        social programs, or infrastructure, the money doesn't enter the economy
        evenly. It is first spent on contractors, employees, and suppliers who,
        in turn, spend it on other goods and services, setting off a ripple of
        rising prices. By the time the money reaches the last participants in
        the economy, prices have already risen, and they find themselves poorer
        in real terms, even if they hold more nominal currency.
        <br />
        <br />
        <b>Unequal Distribution of Inflation</b>
        <br />
        <br />
        To illustrate how inflation affects different groups in the economy
        unevenly, let us consider the real-world example of war-time spending.
        Imagine the government prints large sums of money to pay war
        contractors. These contractors, now flush with new cash, begin to spend
        it on goods and services, perhaps from Group A. Group A now sees
        increased demand and raises prices accordingly. With more income, they
        purchase from Group B, who also raise prices. Each subsequent group in
        the supply chain experiences this sequence: prices rise, incomes rise,
        and the money spreads throughout the economy. However, by the time it
        reaches the later groups, much of the price adjustment has already
        occurred. The purchasing power of the new money has already been eroded
        by the time it reaches them.
        <br />
        This process, known as the Cantillon Effect, explains why inflation
        always benefits some groups at the expense of others. The first
        recipients of the newly created money are the primary beneficiaries.
        Their income increases before prices rise, allowing them to buy more
        goods and services while the money still holds its old value. As the new
        money continues to circulate, each group receives it later and finds
        that prices have already risen. By the time the average consumer, the
        retiree, or the fixed-salary employee receives any of this new money,
        their real purchasing power has decreased. Inflation thus creates
        temporary winners and long-term losers.
        <br />
        This mechanism does more than just shift wealth; it also distorts the
        structure of production. Businesses that serve the early recipients of
        new money receive artificial signals that demand is increasing. They
        expand operations, hire new workers, and invest in machinery, all based
        on a demand that is not grounded in real savings or long-term consumer
        preferences. When the inflationary cycle winds down or when interest
        rates are raised to combat rising prices, these projects often become
        unsustainable. Businesses are forced to downsize or shut down entirely,
        workers are laid off, and capital is lost. These are the famous "boom
        and bust" cycles that characterize inflationary economies.
        <br />
        Inflation also erodes social trust. When people no longer believe that
        money holds stable value over time, they begin to behave differently.
        Savers move their wealth into assets like real estate or gold, driving
        up prices in those markets. Investors chase high-return opportunities,
        often taking on undue risk. The financial system becomes more
        speculative and less grounded in productive investment. Worst of all,
        the poorest members of society, who have no access to hedging
        instruments or foreign currencies, are hit the hardest. They find
        themselves priced out of basic necessities while their wages remain
        stagnant. Inflation is often called a "hidden tax," but it is more than
        that, it is a tax that disproportionately affects the most vulnerable.
        <br />
        <br />
        <b>Inflation as a Tool of Hidden Exploitation</b>
        <br />
        <br />
        The greatest danger of inflation is not just its economic consequences
        but its political convenience. Inflation allows governments to raise
        revenue without raising taxes. By printing money to cover budget
        deficits, they can avoid unpopular fiscal decisions while still
        expanding public programs or funding wars. This erodes the principle of
        accountability. Citizens may notice rising prices, but few connect this
        directly to the actions of their central banks or treasury departments.
        Inflation thus becomes a tool of exploitation that bypasses democratic
        oversight. It creates an illusion of prosperity while silently draining
        the value of people’s savings and earnings.
        <br />
        Furthermore, inflation weakens the moral fabric of a society. It teaches
        people to think in nominal rather than real terms. When prices rise, and
        wages follow, people often feel richer without realizing that their
        purchasing power may have stayed the same or even declined. It promotes
        short-term thinking and encourages debt-driven consumption. Why save for
        the future when money loses value each year? Why invest in productive
        enterprises when speculative assets offer quicker returns? Over time,
        inflation erodes the very virtues that build strong economies: prudence,
        patience, long-term planning, and trust.
        <br />
        Historically, nations that have resorted to inflationary finance on a
        large scale have always faced painful corrections. From the
        hyperinflation of Weimar Germany to the more recent examples in
        Zimbabwe, Venezuela, and Argentina, the path is always the same. It
        begins with "stimulus," then accelerates into dependency, followed by
        price distortions, supply shortages, political unrest, and finally,
        economic collapse. Even moderate inflation, when sustained over decades,
        can wipe out the value of lifetime savings, destroy pension systems, and
        cripple the middle class.
        <br />
        The solution is not merely to "control inflation" through interest rates
        or monetary targets but to eliminate the underlying incentives for
        governments to inflate in the first place. This requires fiscal
        discipline, sound money, and an informed citizenry. Only when people
        understand that money is a claim on wealth and not wealth itself can
        they resist the siren call of inflationary promises. Inflation is not a
        technical issue to be managed by central bankers. It is a moral issue
        that touches every household, every savings account, and every act of
        economic cooperation. When money is debased, society itself begins to
        unravel.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today, we learned that inflation is the gradual rise in prices over
        time, which reduces the purchasing power of money. We explored how
        inflation occurs when more money is added into the economy without a
        corresponding increase in goods and services, leading to each unit of
        currency losing value. We also discussed how inflation affects
        individuals, businesses, and the broader economy in different ways
        helping some while hurting others. Understanding inflation is essential
        to making sense of everyday price changes and the decisions made by
        governments and central banks.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        Now that we’ve seen how inflation chips away at the purchasing power of
        money, we’ll turn to the question of savings. In Day 41, we’ll explore
        how inflation quietly eats into the value of what we set aside, why
        simply keeping money in the bank might not be enough, and how different
        approaches to saving can either protect or endanger our future financial
        security.
      </p>
      <h2>Test your understanding</h2>
     
     
       {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics40;
